import get from 'lodash/get';

import type { ExistingVolumeOptionItem } from '../../components/existingVolumeSelect';
import type { VendorDeviceMeta } from '../../components/vendorDevice';
import { normalizeVendorDeviceMeta } from '../../components/vendorDevice';
import { parseGiSize } from '../../utils/diskSize';
import type { DataDiskRecord, StorageDataDiskCardItem } from './types';

export function normalizeDataDiskList(raw: unknown): DataDiskRecord[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => normalizeDataDiskRecord(item)).filter((item): item is DataDiskRecord => Boolean(item));
}

function normalizeDataDiskRecord(raw: unknown): DataDiskRecord | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const record = raw as DataDiskRecord;
  if (!record.diskName) return undefined;
  const parsedSize = Number(record.diskSize);
  const diskSize =
    record.diskSize !== undefined &&
    record.diskSize !== null &&
    String(record.diskSize).trim() !== '' &&
    Number.isFinite(parsedSize) &&
    parsedSize > 0
      ? parsedSize
      : undefined;
  return {
    ...record,
    ...(diskSize != null ? { diskSize } : { diskSize: undefined }),
    sourceKind: record.sourceKind === 'existing' ? 'existing' : 'new',
  };
}

export function diskToCardItem(disk: DataDiskRecord, storageClassLabelMap?: Record<string, string>): StorageDataDiskCardItem {
  return {
    diskName: disk.diskName,
    diskNickname: disk.diskNickname || disk.diskName,
    diskSize: disk.diskSize,
    storageClass: disk.storageClass,
    storageClassLabel: disk.storageClass ? storageClassLabelMap?.[disk.storageClass] || disk.storageClass : undefined,
    reclaimPolicy: disk.reclaimPolicy,
    locked: disk.locked,
  };
}

export function buildDataDiskFromCreateForm(values: {
  diskName: string;
  diskSize?: number;
  storageClass?: string;
  reclaimPolicy?: string;
  pricing?: VendorDeviceMeta;
}): DataDiskRecord {
  const diskName = String(values.diskName || '').trim();
  return {
    diskName,
    diskNickname: diskName,
    ...(values.diskSize != null ? { diskSize: values.diskSize } : {}),
    storageClass: values.storageClass,
    reclaimPolicy: values.reclaimPolicy,
    sourceKind: 'new',
    pricing: normalizeVendorDeviceMeta(values.pricing),
  };
}

export function buildDataDiskFromExistingSelection(selected: ExistingVolumeOptionItem): DataDiskRecord {
  const origin = selected.origin;
  const diskSize = parseGiSize(get(origin, 'spec.pvc.resources.requests.storage'));
  return {
    diskName: get(origin, 'metadata.name', ''),
    diskNickname: get(origin, 'spec.name', ''),
    diskSize,
    storageClass: get(origin, 'spec.pvc.storageClassName'),
    reclaimPolicy: get(origin, 'spec.recyclePolicy') || get(origin, 'spec.reclaimPolicy'),
    sourceKind: 'existing',
  };
}

export function mergeEditedDataDisk(current: DataDiskRecord | undefined, next: DataDiskRecord): DataDiskRecord {
  return {
    ...next,
    diskSize: next.diskSize ?? current?.diskSize,
    sourceKind: current?.sourceKind ?? next.sourceKind,
    locked: current?.locked,
    pricing: next.pricing ?? current?.pricing,
  };
}

/** 新建数据盘未选存储类（已有盘跳过） */
function isNewDataDiskMissingStorageClass(disk: DataDiskRecord): boolean {
  if (disk.sourceKind === 'existing') return false;
  return !String(disk.storageClass ?? '').trim();
}

/** 新建数据盘列表中缺少存储类的下标 */
export function dataDiskIndexesMissingStorageClass(disks: unknown): number[] {
  return normalizeDataDiskList(disks).flatMap((disk, index) => (isNewDataDiskMissingStorageClass(disk) ? [index] : []));
}
